/*
 * Benutzer einer Anlage (Web-HMI, 18.10.2026): wer die HMI dieser Anlage aus
 * der Ferne sehen oder bedienen darf. Endkunden bekommen damit keinen Zugang
 * zum Dashboard; sie sehen nur die Web-HMI der zugewiesenen Anlagen. Ist die
 * E-Mail-Adresse noch unbekannt, legt HVACloud einen vorbereiteten Benutzer in
 * der Gruppe Endkunden an, der bei seiner ersten Anmeldung mit derselben
 * bestaetigten E-Mail verknuepft wird.
 */

import { useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import { showToast } from '../components/Toaster';
import { PlantPortalSection } from './PlantPortalSection';
import './PlantUsersPage.css';

interface PlantUser {
  userId: string;
  displayName: string;
  email: string | null;
  access: 'bedienen' | 'ansehen';
  assignedAt: string;
  signedIn: boolean;
}

export function PlantUsersPage({ id }: { id?: string }) {
  const [tick, setTick] = useState(0);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [access, setAccess] = useState<'bedienen' | 'ansehen'>('bedienen');
  const [busy, setBusy] = useState(false);
  const list = useAsync((signal) => api.get<PlantUser[]>(`/api/plants/${id}/users`, signal), [id, tick]);

  if (!id) return <AppShell title="Benutzer"><p>Keine Anlagen-ID in der URL.</p></AppShell>;

  const add = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ created: boolean; displayName: string }>(`/api/plants/${id}/users`,
        { email, displayName: name || null, access });
      showToast({ tone: 'success', title: r.created ? 'Benutzer angelegt und zugewiesen' : 'Benutzer zugewiesen',
        message: r.created ? 'Er braucht noch ein Konto mit dieser E-Mail-Adresse, dann sieht er die Anlage nach der Anmeldung.' : undefined });
      setEmail(''); setName('');
      setTick((t) => t + 1);
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht zugewiesen',
        message: e instanceof ApiError ? ((e.body as { error?: string } | null)?.error ?? `HTTP ${e.status}`) : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u: PlantUser) => {
    if (!window.confirm(`${u.displayName} den Zugriff auf diese Anlage entziehen?`)) return;
    try {
      await api.delete(`/api/plants/${id}/users/${u.userId}`);
      setTick((t) => t + 1);
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht entfernt', message: e instanceof ApiError ? `HTTP ${e.status}` : String(e) });
    }
  };

  return (
    <AppShell title="Benutzer">
      <PlantSubNav plantId={id} />
      <div class="hv-plantusers">
        <p class="hv-plantusers__hint">
          Diese Benutzer sehen die Anzeige der Anlage in der Web-HMI, am Handy oder im Browser. Das Dashboard
          sehen sie nicht. „Bedienen“ erlaubt Sollwerte der Stufe normal, „Ansehen“ nur lesen.
          Eine Sitzung endet nach einer festen Zeit; für eine dauerhafte Anzeige ist das Bedienfeld an der Anlage da.
        </p>
        <section class="hv-plantusers__add">
          <h3>Benutzer hinzufügen</h3>
          <div class="hv-plantusers__row">
            <input type="email" placeholder="E-Mail-Adresse" value={email}
                   onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />
            <input placeholder="Name (optional)" value={name}
                   onInput={(e) => setName((e.target as HTMLInputElement).value)} />
            <select value={access} onChange={(e) => setAccess((e.target as HTMLSelectElement).value as 'bedienen' | 'ansehen')}>
              <option value="bedienen">Bedienen</option>
              <option value="ansehen">Ansehen</option>
            </select>
            <button type="button" class="hv-btn hv-btn--primary" disabled={busy || !email.includes('@')} onClick={() => void add()}>
              {busy ? 'Weise zu…' : 'Zuweisen'}
            </button>
          </div>
        </section>
        <PlantPortalSection plantId={id} onChanged={() => setTick((t) => t + 1)} />
        <section>
          <h3>Zugewiesen</h3>
          {list.status === 'loading' && <p class="muted">Lade…</p>}
          {list.status === 'error' && <p class="hv-plantusers__warn">Liste nicht ladbar. Dafür braucht es die Berechtigung „users.admin“.</p>}
          {list.status === 'ok' && list.data.length === 0 && <p class="muted">Noch niemand.</p>}
          {list.status === 'ok' && list.data.length > 0 && (
            <table class="hv-plantusers__table">
              <thead><tr><th>Name</th><th>E-Mail</th><th>Zugriff</th><th>Angemeldet</th><th /></tr></thead>
              <tbody>
                {list.data.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.displayName}</td>
                    <td>{u.email ?? '–'}</td>
                    <td>{u.access === 'ansehen' ? 'Ansehen' : 'Bedienen'}</td>
                    <td>{u.signedIn ? 'ja' : 'noch nie'}</td>
                    <td><button type="button" class="hv-btn hv-btn--sm" onClick={() => void remove(u)}>Entfernen</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </AppShell>
  );
}
